/**
 * Painel de acompanhamento da adesão ao boleto único (tarefa #5579) — lógica pura.
 *
 * O painel responde as quatro perguntas do Felipe: quem já é adepto, quem não aceitou
 * ainda, se o disparo ocorreu, e quem tem débito automático pendente de resolução. Cada
 * uma delas já tem dono no sistema:
 *
 *   - adesão ligada .......... `unidades_consumidoras.boleto_unico` (o que a emissão lê)
 *   - aceite ................. `boleto_unico_aceites` + espelhos na UC (#5576)
 *   - disparo ................ `boleto_unico_disparos` + espelhos na UC (#5578 escreve)
 *   - débito automático ...... classificação sobre `faturas` (#5570, auditada em PDF)
 *
 * ESTE ARQUIVO NÃO REIMPLEMENTA NENHUMA DELAS. Ele junta as quatro e responde uma quinta,
 * que é a razão do painel existir: **qual é a próxima ação nesta UC, hoje?** Sem isso o
 * painel seria seis colunas de status e a operação continuaria cruzando na cabeça.
 *
 * Por que a próxima ação é UMA, e não um conjunto de etiquetas: a tela é fila de
 * trabalho, não relatório. Uma UC sem disparo e com débito automático desconhecido não
 * precisa de duas decisões — precisa de disparo; o débito automático se resolve dentro do
 * próprio fluxo de aceite (foi a conclusão da #5570: não abrir canal novo só para isso).
 * A ordem de prioridade em `derivarFila` é essa lógica escrita uma vez, testada, e lida
 * igual pelo painel, pelo contador dos filtros e pela exportação.
 */

import {
  classificarUc,
  indexarFaturasPorUc,
  janelaMeses,
  ucElegivel,
  type FaturaDebitoAutomatico,
  type MotivoDebitoAutomatico,
  type StatusDebitoAutomatico,
  type UcDebitoAutomatico,
} from './debitoAutomaticoEquatorial.ts';
import {
  derivarStatusAceite,
  vigenciaJaComecou,
  VIGENCIA_PADRAO,
  type StatusAceiteBoletoUnico,
} from './aceite.ts';
import {
  decidirCanalAviso,
  type CanalAviso,
  type ContatoUcAviso,
} from '../../../supabase/functions/_shared/boletoUnicoAviso.ts';

/** Rótulos de mês como `mes_referencia` guarda ('JUL/2026') — texto, não data. */
const MESES_REFERENCIA = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/**
 * Os `mes_referencia` da janela, do mais recente para o mais antigo.
 *
 * Existe porque `mes_referencia` é texto livre ('JUL/2026') e não aceita comparação de
 * intervalo em SQL: para buscar só as faturas da janela é preciso montar a lista exata e
 * usar `.in(...)`. Varrer a tabela inteira para filtrar no cliente traria ~6 mil linhas
 * em vez de ~2,3 mil.
 */
export function mesesReferenciaDaJanela(hoje: Date, meses = 3): string[] {
  return [...janelaMeses(hoje, meses)]
    .sort((a, b) => b.localeCompare(a))
    .map(chave => {
      const [ano, mes] = chave.split('-');
      return `${MESES_REFERENCIA[Number(mes) - 1]}/${ano}`;
    });
}

/**
 * A fila de trabalho da UC — a próxima ação, uma só.
 *
 * Ordem de gravidade, não de etapa: as duas primeiras são erro já em produção, as outras
 * são o funil normal andando.
 */
export type FilaAdesao =
  /** Cobra Vigor + Equatorial e a Equatorial debita a conta do cliente: cobrança em dobro. */
  | 'risco_cobranca_dobrada'
  /** Está no boleto único sem aceite registrado. */
  | 'regularizar_sem_aceite'
  /** A última tentativa de aviso falhou e nenhuma deu certo. O motivo diz o que corrigir. */
  | 'falha_disparo'
  /** Elegível e ninguém avisou ainda. */
  | 'pendente_disparo'
  /** Na fila de envio (#5578): vai sair no horário agendado, nada a fazer. */
  | 'disparo_agendado'
  /** Avisado, sem resposta. */
  | 'aguardando_aceite'
  /** Aceitou, mas a cláusula só vale a partir de OUT/2026 — liberar hoje adianta a virada. */
  | 'aguardando_vigencia'
  /** Aceitou e tem débito automático: a adesão depende do cancelamento na Equatorial. */
  | 'exigir_cancelamento'
  /** Aceitou e não sabemos se tem débito automático — confirmar antes de liberar. */
  | 'confirmar_debito'
  /** Aceitou, sem débito automático, vigência já corrente: só falta ligar. */
  | 'pronta_para_liberar'
  /** Liberada com aceite e sem pendência. Nada a fazer. */
  | 'em_dia';

export interface RotuloFila {
  texto: string;
  /** Como a tela trata visualmente. `perigo` é dinheiro errado já saindo. */
  tom: 'perigo' | 'alerta' | 'espera' | 'acao' | 'ok';
  /** O que a pessoa faz a seguir, em uma frase. Vai no tooltip e na exportação. */
  ajuda: string;
}

export const ROTULOS_FILA: Record<FilaAdesao, RotuloFila> = {
  risco_cobranca_dobrada: {
    texto: 'Cobrança em dobro',
    tom: 'perigo',
    ajuda: 'A UC está no boleto único e a última fatura acusa débito automático na Equatorial. Desligue a adesão ou registre o cancelamento atestado.',
  },
  regularizar_sem_aceite: {
    texto: 'Liberada sem aceite',
    tom: 'alerta',
    ajuda: 'Está cobrando unificado sem registro de que o cliente aceitou. Registre o aceite pela UC ou desligue a adesão.',
  },
  falha_disparo: {
    texto: 'Falha no aviso',
    tom: 'alerta',
    ajuda: 'O aviso não chegou ao cliente. Veja o motivo, corrija o cadastro se for o caso e dispare de novo.',
  },
  disparo_agendado: {
    texto: 'Na fila de envio',
    tom: 'espera',
    ajuda: 'O aviso já está agendado e sai sozinho, um a cada 2 a 3 minutos. Dá para parar enquanto não saiu.',
  },
  pendente_disparo: {
    texto: 'Pendente de disparo',
    tom: 'acao',
    ajuda: 'Cliente elegível que ainda não recebeu o aviso com o link do Portal.',
  },
  aguardando_aceite: {
    texto: 'Aguardando aceite',
    tom: 'espera',
    // "Enviado à UaisyChat", não "entregue": a UaisyChat só enfileira o pedido e manda depois.
    // Falha que acontece lá dentro (variável vazia, número sem WhatsApp, recusa da Meta) não
    // volta para o VigorHub — confirmado no teste de 14/09/2026. Dizer "entregue" seria mentir.
    ajuda: 'Aviso enviado à UaisyChat e cliente ainda não aceitou. Segue no modelo atual até aceitar. Se o cliente disser que não recebeu, confira a conversa na UaisyChat.',
  },
  aguardando_vigencia: {
    texto: 'Aguardando vigência',
    tom: 'espera',
    ajuda: 'Aceite registrado, mas a cláusula vale a partir de OUT/2026. Liberar antes dessa data faz o boleto do mês corrente já sair unificado.',
  },
  exigir_cancelamento: {
    texto: 'Exigir cancelamento',
    tom: 'alerta',
    ajuda: 'Aceitou, mas tem débito automático na Equatorial. A adesão só pode ser liberada com o cancelamento confirmado com o cliente.',
  },
  confirmar_debito: {
    texto: 'Confirmar débito automático',
    tom: 'alerta',
    ajuda: 'Aceitou, mas não há fatura lida que confirme se ele é debitado pela Equatorial. Confirme antes de liberar, senão o risco é cobrar duas vezes.',
  },
  pronta_para_liberar: {
    texto: 'Pronta para liberar',
    tom: 'acao',
    ajuda: 'Aceite registrado, sem débito automático e dentro da vigência. Ligue o boleto único na UC.',
  },
  em_dia: {
    texto: 'Em dia',
    tom: 'ok',
    ajuda: 'Cobrança unificada ativa, com aceite registrado e sem pendência.',
  },
};

/** Só o que o painel lê da UC, além do que a classificação de débito automático já usa. */
export interface UcPainelAdesao extends UcDebitoAutomatico, ContatoUcAviso {
  id?: number;
  /** UC de empresa parceira fica fora: a Gran tem boleto único próprio, via Asaas. */
  empresa_parceira_id?: string | null;
  boleto_unico_aceite_em?: string | null;
  boleto_unico_aceite_canal?: string | null;
  boleto_unico_disparo_em?: string | null;
  boleto_unico_disparo_canal?: string | null;
}

/**
 * O aceite ativo da UC. Os espelhos na UC bastam para data e canal; a vigência e o
 * atestado de cancelamento só existem aqui — e são exatamente o que decide entre
 * 'aguardando_vigencia', 'exigir_cancelamento' e 'pronta_para_liberar'.
 */
export interface AceiteAtivoPainel {
  uc_id: number;
  aceito_em?: string | null;
  canal?: string | null;
  vigencia_a_partir_de?: string | null;
  cancelamento_atestado_em?: string | null;
  cancelamento_confirmado_em?: string | null;
}

/** Item ainda por sair da fila de envio (`boleto_unico_disparo_fila`, status pendente/enviando). */
export interface FilaAvisoPainel {
  uc_id: number;
  status?: string | null;
  scheduled_at?: string | null;
  lote_id?: string | null;
}

/** Última tentativa FALHA de aviso da UC (`boleto_unico_disparos` com `ok = false`). */
export interface FalhaDisparoPainel {
  uc_id: number;
  enviado_em?: string | null;
  erro?: string | null;
  canal?: string | null;
}

export interface ExtrasPainelAdesao {
  fila?: FilaAvisoPainel[];
  falhas?: FalhaDisparoPainel[];
}

export interface LinhaPainelAdesao {
  uc_id?: number;
  uc_codigo: string;
  outro_numero_uc: string | null;
  cliente: string;
  situacao: string | null;

  boleto_unico: boolean;
  status_aceite: StatusAceiteBoletoUnico;
  aceite_em: string | null;
  aceite_canal: string | null;
  vigencia_a_partir_de: string;
  vigencia_iniciada: boolean;
  cancelamento_atestado_em: string | null;
  cancelamento_confirmado_em: string | null;

  disparo_em: string | null;
  disparo_canal: string | null;
  /** Por onde o aviso sairia hoje, pela mesma regra da edge function. */
  canal_aviso: CanalAviso;
  /** Só com `canal_aviso = 'nenhum'`: o que falta no cadastro. */
  motivo_sem_canal: string | null;
  /** Horário agendado na fila de envio, se estiver lá. */
  disparo_agendado_para: string | null;
  disparo_lote_id: string | null;
  /** Última falha de aviso — só preenchida quando nenhum aviso deu certo. */
  falha_disparo_em: string | null;
  falha_disparo_erro: string | null;

  debito: StatusDebitoAutomatico;
  debito_motivo: MotivoDebitoAutomatico;
  ultimo_mes_avaliado: string | null;
  meses_com_debito_automatico: number;

  fila: FilaAdesao;
}

/**
 * A próxima ação da UC. Ordem de precedência, de cima para baixo — a primeira que casa
 * vence. Mudança aqui muda o painel, os contadores e a exportação de uma vez.
 */
export function derivarFila(
  linha: Omit<LinhaPainelAdesao, 'fila'>,
): FilaAdesao {
  const temAceite = Boolean(linha.aceite_em);

  if (linha.boleto_unico) {
    // Já cobrando unificado. Débito automático aqui é dinheiro errado saindo agora — a
    // menos que alguém tenha atestado o cancelamento (aí a flag da fatura antiga é
    // histórico, e a #5577 já destrava a emissão com base nesse atestado).
    const atestado = Boolean(linha.cancelamento_atestado_em || linha.cancelamento_confirmado_em);
    if (linha.debito === 'confirmado_sim' && !atestado) return 'risco_cobranca_dobrada';
    if (!temAceite) return 'regularizar_sem_aceite';
    return 'em_dia';
  }

  if (temAceite) {
    // A vigência vem antes do resto de propósito: a emissão (`equatorialNoBoleto`) olha
    // só a flag `boleto_unico`, não a data. Dizer "pronta para liberar" em setembro
    // faria alguém ligar a UC e o boleto do mês corrente sair unificado antes do que a
    // cláusula prometeu ao cliente.
    if (!linha.vigencia_iniciada) return 'aguardando_vigencia';
    if (linha.debito === 'confirmado_sim' && !linha.cancelamento_atestado_em) return 'exigir_cancelamento';
    // 'desconhecido' NÃO fica mais parado esperando alguém confirmar (decisão do Victor,
    // 13/10/2026): o banco libera essas UCs sozinho logo após o aceite. Só o débito
    // automático CONFIRMADO sem cancelamento segura a liberação. Esta linha, na prática,
    // só aparece no intervalo entre o aceite e a liberação automática.
    return 'pronta_para_liberar';
  }

  // Sem aceite: o débito automático desconhecido não é ação própria — ele se resolve
  // dentro do fluxo de aceite (conclusão da #5570, sem abrir canal novo). O que falta
  // aqui é avisar o cliente.
  if (!linha.disparo_em) {
    // Agendado vem antes da falha: é a tentativa nova de quem já falhou uma vez.
    if (linha.disparo_agendado_para) return 'disparo_agendado';
    if (linha.falha_disparo_em) return 'falha_disparo';
    return 'pendente_disparo';
  }
  return 'aguardando_aceite';
}

export interface OpcoesPainelAdesao {
  hoje: Date;
  /** Janela de competências da classificação de débito automático. Padrão 3, como a #5570. */
  meses?: number;
}

/**
 * A UC entra no painel? Elegível pela situação e da própria Vigor.
 *
 * Parceira fica fora porque tem boleto único próprio: as 68 UCs da Gran que em 14/09/2026
 * apareciam como "liberada sem aceite" estão no boleto único pelo fluxo da parceira
 * (Asaas), não pelo da Vigor — contá-las como erro era alarme falso, e mandar a elas o
 * aviso da Vigor seria convite para um produto que não é o delas.
 */
export function ucNoPainel(uc: UcPainelAdesao): boolean {
  return ucElegivel(uc) && !uc.empresa_parceira_id;
}

/** O que ficou fora por ser de parceira — a tela diz o número, para ninguém achar que sumiu. */
export function contarParceirasForaDoPainel(ucs: UcPainelAdesao[]): number {
  return ucs.filter(uc => ucElegivel(uc) && Boolean(uc.empresa_parceira_id)).length;
}

/**
 * Monta as linhas do painel. Só UCs elegíveis e da Vigor entram — cancelada/pausada não
 * adere, e misturá-las inflaria as filas com trabalho que não existe.
 */
export function montarPainelAdesao(
  ucs: UcPainelAdesao[],
  faturas: FaturaDebitoAutomatico[],
  aceites: AceiteAtivoPainel[],
  opcoes: OpcoesPainelAdesao,
  extras: ExtrasPainelAdesao = {},
): LinhaPainelAdesao[] {
  const janela = janelaMeses(opcoes.hoje, opcoes.meses ?? 3);
  const porUc = indexarFaturasPorUc(faturas);
  const aceitePorUc = new Map<number, AceiteAtivoPainel>();
  for (const a of aceites) {
    if (typeof a.uc_id === 'number') aceitePorUc.set(a.uc_id, a);
  }

  const filaPorUc = new Map<number, FilaAvisoPainel>();
  for (const f of extras.fila || []) {
    if (typeof f.uc_id === 'number') filaPorUc.set(f.uc_id, f);
  }

  // Só a falha MAIS RECENTE de cada UC interessa; a lista pode vir em qualquer ordem.
  const falhaPorUc = new Map<number, FalhaDisparoPainel>();
  for (const f of extras.falhas || []) {
    if (typeof f.uc_id !== 'number') continue;
    const atual = falhaPorUc.get(f.uc_id);
    if (!atual || String(f.enviado_em || '') > String(atual.enviado_em || '')) falhaPorUc.set(f.uc_id, f);
  }

  return ucs.filter(ucNoPainel).map(uc => {
    const classificacao = classificarUc(uc, porUc, janela);
    const aceite = typeof uc.id === 'number' ? aceitePorUc.get(uc.id) : undefined;

    // Data do aceite: o espelho da UC é a leitura rápida; a linha da tabela é a verdade.
    // Divergência entre os dois só aconteceria com escrita manual no espelho, e nesse
    // caso a tabela é quem manda.
    const aceiteEm = aceite?.aceito_em || uc.boleto_unico_aceite_em || null;
    const canal = decidirCanalAviso(uc);
    const naFila = typeof uc.id === 'number' ? filaPorUc.get(uc.id) : undefined;
    const disparoEm = uc.boleto_unico_disparo_em || null;
    // Falha antiga de quem depois recebeu o aviso é histórico, não pendência.
    const falha = !disparoEm && typeof uc.id === 'number' ? falhaPorUc.get(uc.id) : undefined;
    const vigencia = (aceite?.vigencia_a_partir_de || VIGENCIA_PADRAO).slice(0, 10);

    const base: Omit<LinhaPainelAdesao, 'fila'> = {
      uc_id: uc.id,
      uc_codigo: classificacao.uc_codigo,
      outro_numero_uc: classificacao.outro_numero_uc,
      cliente: classificacao.cliente,
      situacao: classificacao.situacao,

      boleto_unico: classificacao.boleto_unico,
      status_aceite: derivarStatusAceite({
        boleto_unico: uc.boleto_unico,
        boleto_unico_aceite_em: aceiteEm,
        boleto_unico_aceite_canal: uc.boleto_unico_aceite_canal,
      }),
      aceite_em: aceiteEm,
      aceite_canal: aceite?.canal || uc.boleto_unico_aceite_canal || null,
      vigencia_a_partir_de: vigencia,
      vigencia_iniciada: vigenciaJaComecou(vigencia, opcoes.hoje),
      cancelamento_atestado_em: aceite?.cancelamento_atestado_em || null,
      cancelamento_confirmado_em: aceite?.cancelamento_confirmado_em || null,

      disparo_em: disparoEm,
      disparo_canal: uc.boleto_unico_disparo_canal || null,
      canal_aviso: canal.canal,
      motivo_sem_canal: canal.motivo,
      disparo_agendado_para: naFila?.scheduled_at || null,
      disparo_lote_id: naFila?.lote_id || null,
      falha_disparo_em: falha?.enviado_em || null,
      falha_disparo_erro: falha ? (falha.erro || 'Falha sem motivo registrado.') : null,

      debito: classificacao.status,
      debito_motivo: classificacao.motivo,
      ultimo_mes_avaliado: classificacao.ultimo_mes_avaliado,
      meses_com_debito_automatico: classificacao.meses_com_debito_automatico,
    };

    return { ...base, fila: derivarFila(base) };
  });
}

/**
 * Os filtros da tela. A #5579 pede três ("pendente de disparo", "aceitou mas não
 * liberado", "débito automático desconhecido"); os outros três existem porque são as
 * únicas formas de a operação achar erro já em produção e de ver o que está pronto.
 *
 * `atencao` junta as duas filas de erro num chip só: são poucas linhas e ninguém quer
 * dois cliques para ver "o que está errado agora".
 */
export type GrupoFiltroPainel =
  | 'todos'
  | 'atencao'
  | 'pendente_disparo'
  | 'falha_disparo'
  | 'na_fila'
  | 'aguardando_aceite'
  | 'aceito_nao_liberado'
  | 'debito_desconhecido'
  | 'liberado';

export interface DefinicaoGrupo {
  id: GrupoFiltroPainel;
  rotulo: string;
  ajuda: string;
  /** Nenhum predicado = todos. */
  predicado?: (l: LinhaPainelAdesao) => boolean;
  /**
   * Chip que só aparece com contagem maior que zero. Para estados passageiros da fila de
   * envio: um chip "Na fila (0)" fixo na tela é ruído na maior parte do tempo.
   */
  somenteComItens?: boolean;
}

export const GRUPOS_FILTRO: DefinicaoGrupo[] = [
  {
    id: 'todos',
    rotulo: 'Todas',
    ajuda: 'Todas as UCs elegíveis à adesão (ativas, em ciclo ou reativadas).',
  },
  {
    id: 'atencao',
    rotulo: 'Atenção',
    ajuda: 'Erro já em produção: cobrança em dobro possível, ou adesão ligada sem aceite do cliente.',
    predicado: l => l.fila === 'risco_cobranca_dobrada' || l.fila === 'regularizar_sem_aceite',
  },
  {
    id: 'pendente_disparo',
    rotulo: 'Pendente de disparo',
    ajuda: 'Elegíveis que ainda não receberam o aviso com o link do Portal.',
    predicado: l => l.fila === 'pendente_disparo',
  },
  {
    id: 'falha_disparo',
    rotulo: 'Falha no aviso',
    ajuda: 'O aviso não chegou. Cada linha diz o motivo: cadastro sem canal, n8n ou UaisyChat fora, e-mail recusado.',
    predicado: l => l.fila === 'falha_disparo',
    somenteComItens: true,
  },
  {
    id: 'na_fila',
    rotulo: 'Na fila de envio',
    ajuda: 'Avisos agendados que ainda não saíram. Saem um a cada 2 a 3 minutos.',
    predicado: l => l.fila === 'disparo_agendado',
    somenteComItens: true,
  },
  {
    id: 'aguardando_aceite',
    rotulo: 'Aguardando aceite',
    ajuda: 'Avisadas e sem resposta. Continuam no modelo atual.',
    predicado: l => l.fila === 'aguardando_aceite',
  },
  {
    id: 'aceito_nao_liberado',
    rotulo: 'Aceitou, não liberado',
    ajuda: 'Aceite registrado e adesão ainda desligada — inclui o que espera vigência, cancelamento ou confirmação.',
    predicado: l => Boolean(l.aceite_em) && !l.boleto_unico,
  },
  {
    id: 'debito_desconhecido',
    rotulo: 'Débito automático desconhecido',
    ajuda: 'Não há fatura lida que confirme se a Equatorial debita a conta do cliente. Corta todas as etapas.',
    predicado: l => l.debito === 'desconhecido',
  },
  {
    id: 'liberado',
    rotulo: 'Liberadas',
    ajuda: 'Já cobrando Vigor + Equatorial no mesmo boleto.',
    predicado: l => l.boleto_unico,
  },
];

export function contarGrupos(linhas: LinhaPainelAdesao[]): Record<GrupoFiltroPainel, number> {
  const contagem = {} as Record<GrupoFiltroPainel, number>;
  for (const grupo of GRUPOS_FILTRO) {
    contagem[grupo.id] = grupo.predicado ? linhas.filter(grupo.predicado).length : linhas.length;
  }
  return contagem;
}

/** Busca por cliente, UC ou segundo número da UC — os três jeitos de identificar a linha. */
export function casaBusca(linha: LinhaPainelAdesao, busca: string): boolean {
  const termo = busca.trim().toLowerCase();
  if (!termo) return true;
  return [linha.cliente, linha.uc_codigo, linha.outro_numero_uc]
    .some(v => (v || '').toLowerCase().includes(termo));
}

/**
 * Ordena pela gravidade da fila e, dentro dela, por cliente. Sem isso a linha que precisa
 * de ação hoje fica na posição 400 da tabela, e o painel só serve para contar.
 */
const PESO_FILA: Record<FilaAdesao, number> = {
  risco_cobranca_dobrada: 0,
  regularizar_sem_aceite: 1,
  falha_disparo: 1.5,
  pronta_para_liberar: 2,
  exigir_cancelamento: 3,
  confirmar_debito: 4,
  aguardando_vigencia: 5,
  pendente_disparo: 6,
  disparo_agendado: 6.5,
  aguardando_aceite: 7,
  em_dia: 8,
};

export function filtrarPainel(
  linhas: LinhaPainelAdesao[],
  grupo: GrupoFiltroPainel,
  busca = '',
): LinhaPainelAdesao[] {
  const definicao = GRUPOS_FILTRO.find(g => g.id === grupo);
  const predicado = definicao?.predicado;
  return linhas
    .filter(l => (predicado ? predicado(l) : true) && casaBusca(l, busca))
    .sort((a, b) => {
      const peso = PESO_FILA[a.fila] - PESO_FILA[b.fila];
      if (peso !== 0) return peso;
      return a.cliente.localeCompare(b.cliente, 'pt-BR');
    });
}

export interface ResumoPainelAdesao {
  elegiveis: number;
  liberadas: number;
  aceitaram_nao_liberadas: number;
  atencao: number;
  pendentes_disparo: number;
  falhas_disparo: number;
  na_fila: number;
  debito_desconhecido: number;
}

export function resumirPainel(linhas: LinhaPainelAdesao[]): ResumoPainelAdesao {
  const contagem = contarGrupos(linhas);
  return {
    elegiveis: contagem.todos,
    liberadas: contagem.liberado,
    aceitaram_nao_liberadas: contagem.aceito_nao_liberado,
    atencao: contagem.atencao,
    pendentes_disparo: contagem.pendente_disparo,
    falhas_disparo: contagem.falha_disparo,
    na_fila: contagem.na_fila,
    debito_desconhecido: contagem.debito_desconhecido,
  };
}

/** Rótulo curto do status de débito automático, igual em tela e na planilha. */
export const ROTULOS_DEBITO: Record<StatusDebitoAutomatico, string> = {
  confirmado_sim: 'Sim',
  confirmado_nao: 'Não',
  desconhecido: 'Desconhecido',
};

/**
 * Por que o status de débito automático é esse. Sem o motivo, "desconhecido" não diz se
 * falta fatura (cobertura do robô) ou se a fatura chegou e não deu para concluir (parser)
 * — e são duas providências diferentes.
 */
export const MOTIVOS_DEBITO: Record<MotivoDebitoAutomatico, string> = {
  flag_debito_automatico: 'A fatura da Equatorial acusa débito automático.',
  boleto_bancario_presente: 'A fatura traz boleto bancário para pagar — logo, não é debitada automaticamente.',
  linha_arrecadacao_presente: 'A fatura traz linha de arrecadação para pagar — logo, não é debitada automaticamente.',
  sem_fatura_na_janela: 'Nenhuma fatura lida na janela avaliada. UC nova ou fora da cobertura do robô.',
  flag_ausente: 'As faturas da janela são anteriores ao campo de débito automático.',
  sem_dados_de_cobranca: 'A fatura foi lida, mas sem linha de cobrança — não dá para concluir.',
};

/** Rótulo do canal de disparo. Mesmo formato dos canais de aceite. */
export const ROTULOS_CANAL_DISPARO: Record<string, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  manual: 'Registrado pela Vigor',
};

export function rotuloCanalDisparo(canal?: string | null): string {
  return ROTULOS_CANAL_DISPARO[(canal || '').trim()] || '—';
}

// ─────────────────────────────── disparo em ondas (#5578) ───────────────────────────────

/**
 * Pode entrar numa onda? Só quem nunca foi avisado com sucesso: pendente de disparo, ou
 * cuja última tentativa falhou. O servidor refaz a pergunta ao enfileirar
 * (`enfileirar_aviso_boleto_unico`); isto aqui só evita oferecer na tela o que ele recusaria.
 */
export function podeDisparar(linha: LinhaPainelAdesao): boolean {
  return linha.fila === 'pendente_disparo' || linha.fila === 'falha_disparo';
}

export interface ResumoCanaisOnda {
  total: number;
  whatsapp: number;
  email: number;
  /** Vão para a fila e voltam como falha "sem canal" — é assim que viram cadastro a corrigir. */
  sem_canal: number;
}

/** Por onde a onda vai sair, para a confirmação dizer antes de apertar. */
export function resumirCanaisOnda(linhas: LinhaPainelAdesao[]): ResumoCanaisOnda {
  const resumo: ResumoCanaisOnda = { total: linhas.length, whatsapp: 0, email: 0, sem_canal: 0 };
  for (const l of linhas) {
    if (l.canal_aviso === 'whatsapp') resumo.whatsapp++;
    else if (l.canal_aviso === 'email') resumo.email++;
    else resumo.sem_canal++;
  }
  return resumo;
}

/** Motivos que o servidor devolve para UC que ficou fora da onda, em português de tela. */
export const MOTIVOS_FORA_DA_ONDA: Record<string, string> = {
  nao_encontrada: 'UC não encontrada',
  parceira: 'UC de empresa parceira',
  nao_elegivel: 'deixou de ser elegível',
  ja_no_boleto_unico: 'já está no boleto único',
  ja_aceitou: 'cliente já aceitou',
  ja_avisada: 'já recebeu o aviso',
  ja_na_fila: 'já estava na fila',
};
